/**
 * The orchestration flows (D-17 / PM decision: `createAsyncThunk` owns the
 * offline queue, RTK Query owns server state).
 *
 * Every flow obeys the same order — **IndexedDB first, then dispatch, then the
 * network** — and every failure has exactly one of three shapes:
 *
 * | Failure | What happens |
 * |---|---|
 * | The server refused this punch | moved to `failedPunches`, surfaced (I-7) |
 * | The network / the server broke | stays queued, `attempts + 1`, retried |
 * | The punch was already recorded | removed silently (`idempotentReplay`, `duplicate`) |
 *
 * A punch is never edited, never auto-closed and never dropped.
 */
import { createAsyncThunk, type Reducer } from '@reduxjs/toolkit';
import {
  getApiClientRuntime,
  isApiError,
  logout,
  type AppDispatchOf,
  type RootStateOf,
} from '@saniplus/api-client';
import type {
  ErrorCode,
  MeContextResponse,
  PunchInRequest,
  PunchOutRequest,
  PunchResultResponse,
  PunchSyncResponse,
  SyncPunchItem,
} from '@saniplus/contracts';

import { buildCapturedAt, isAnchorReconstructed, isOnline, nowIso } from './clock';
import {
  acknowledgeFailedPunch,
  deleteQueuedPunch,
  getQueuedPunch,
  listAllQueuedPunches,
  listFailedPunches,
  listQueuedPunches,
  moveToFailed,
  patchQueuedPunch,
  putQueuedPunch,
  readCachedContext,
  writeCachedContext,
  type FailedPunch,
  type QueuedPunch,
} from './db';
import { captureGps, GPS_TIMEOUT_MS } from './gps';
import { newClientPunchUuid } from './ids';
import { punchApi } from './punchApi';
import { isPunchRefusal } from './refusals';
import {
  failedAcknowledged,
  punchConfirmed,
  punchQueued,
  punchRefused,
  punchRetryScheduled,
  punchSubmitting,
  punchSynced,
  queueCleared,
  queueRehydrated,
  summarize,
  summarizeFailed,
  syncFinished,
  syncStarted,
  onlineChanged,
  type QueueState,
} from './queueSlice';

/**
 * The store shape these thunks need. The app's real root state has more
 * slices, which is fine — it stays assignable to this one.
 */
type OfflineReducers = { offlineQueue: Reducer<QueueState> };
export type OfflineRootState = RootStateOf<OfflineReducers>;
export type OfflineDispatch = AppDispatchOf<OfflineReducers>;

interface ThunkConfig {
  state: OfflineRootState;
  dispatch: OfflineDispatch;
}

/**
 * The owner every queue operation is scoped to: the signed-in user, or `null`
 * when nobody is. `null` is never a wildcard — a thunk that cannot name an
 * owner does nothing at all, because the alternative is sending one worker's
 * punch under another's token (see `QueuedPunch.ownerUserId`).
 */
function currentOwner(getState: () => OfflineRootState): string | null {
  const { auth } = getState();
  return auth.user?.id ?? null;
}

export type SubmitOutcome =
  /** The server recorded it (or replayed the original). */
  | 'confirmed'
  /** The server refused it; it is in `failedPunches` and must be shown. */
  | 'refused'
  /** Still in `punchQueue`; the scheduler will try again. */
  | 'queued'
  /** Another submitter owns the row right now. */
  | 'in_flight'
  /** Already gone from the queue — confirmed or refused by someone else. */
  | 'gone';

export interface SubmitResult {
  readonly clientPunchUuid: string;
  readonly outcome: SubmitOutcome;
  readonly recordedAt: string | null;
  readonly idempotentReplay: boolean;
  readonly refusalCode: ErrorCode | null;
}

export type EnqueueArgs =
  /** D-08: an IN always names its shift. The type makes the omission impossible. */
  | { readonly type: 'in'; readonly shiftId: string }
  /** An OUT resolves its session server-side; `shiftId` is local bookkeeping. */
  | { readonly type: 'out'; readonly shiftId?: string };

function result(
  clientPunchUuid: string,
  outcome: SubmitOutcome,
  extra: Partial<Omit<SubmitResult, 'clientPunchUuid' | 'outcome'>> = {},
): SubmitResult {
  return {
    clientPunchUuid,
    outcome,
    recordedAt: extra.recordedAt ?? null,
    idempotentReplay: extra.idempotentReplay ?? false,
    refusalCode: extra.refusalCode ?? null,
  };
}

function gpsFieldsOf(record: QueuedPunch): Pick<PunchOutRequest, 'gps' | 'gpsStatus'> {
  // `gps` XOR `gpsStatus`, enforced by the frozen schema. A record with
  // neither is a client bug, and `unavailable` is the honest reading of it.
  if (record.gps !== undefined) return { gps: record.gps };
  return { gpsStatus: record.gpsStatus ?? 'unavailable' };
}

function punchOutBodyOf(record: QueuedPunch): PunchOutRequest {
  return {
    clientPunchUuid: record.clientPunchUuid,
    deviceUuid: record.deviceUuid,
    ...(record.capturedAt === undefined ? {} : { capturedAt: record.capturedAt }),
    ...gpsFieldsOf(record),
  };
}

/** `null` for a corrupt IN row with no shift — refused locally, never sent. */
function punchInBodyOf(record: QueuedPunch): PunchInRequest | null {
  if (record.shiftId === undefined) return null;
  return { ...punchOutBodyOf(record), shiftId: record.shiftId };
}

function syncItemOf(record: QueuedPunch): SyncPunchItem | null {
  if (record.type === 'in' && record.shiftId === undefined) return null;
  return {
    type: record.type === 'in' ? 'IN' : 'OUT',
    clientPunchUuid: record.clientPunchUuid,
    // Only an IN carries a shift on the wire (`SyncPunchItemSchema`).
    ...(record.type === 'in' && record.shiftId !== undefined ? { shiftId: record.shiftId } : {}),
    deviceUuid: record.deviceUuid,
    ...(record.capturedAt === undefined ? {} : { capturedAt: record.capturedAt }),
    ...gpsFieldsOf(record),
  };
}

/**
 * One request, and the mutation cache entry disposed of immediately after.
 * `reset()` is what keeps the promise "never cache a punch response": without
 * it the result of the last punch sits in the store until the app reloads.
 */
async function sendPunch(
  dispatch: OfflineDispatch,
  record: QueuedPunch,
  body: PunchInRequest | PunchOutRequest,
): Promise<PunchResultResponse> {
  if (record.type === 'in') {
    const request = dispatch(punchApi.endpoints.punchIn.initiate(body as PunchInRequest));
    try {
      return await request.unwrap();
    } finally {
      request.reset();
    }
  }
  const request = dispatch(punchApi.endpoints.punchOut.initiate(body));
  try {
    return await request.unwrap();
  } finally {
    request.reset();
  }
}

async function refuseLocally(
  dispatch: OfflineDispatch,
  clientPunchUuid: string,
  code: ErrorCode,
  details?: unknown,
): Promise<FailedPunch | null> {
  const failed = await moveToFailed(clientPunchUuid, {
    refusalCode: code,
    refusedAt: nowIso(),
    ...(details === undefined ? {} : { details }),
  });
  if (failed !== null) dispatch(punchRefused(summarizeFailed(failed)));
  return failed;
}

async function keepForRetry(
  dispatch: OfflineDispatch,
  record: QueuedPunch,
  lastError: string,
): Promise<void> {
  const attempts = record.attempts + 1;
  await patchQueuedPunch(record.clientPunchUuid, { state: 'pending', attempts, lastError });
  dispatch(punchRetryScheduled({ clientPunchUuid: record.clientPunchUuid, attempts, lastError }));
}

async function handleSendFailure(
  dispatch: OfflineDispatch,
  record: QueuedPunch,
  error: unknown,
): Promise<SubmitResult> {
  if (isApiError(error) && isPunchRefusal(error.code)) {
    await refuseLocally(dispatch, record.clientPunchUuid, error.code, error.details);
    return result(record.clientPunchUuid, 'refused', { refusalCode: error.code });
  }
  const code = isApiError(error) ? error.code : 'SERVICE_UNAVAILABLE';
  // The row is already durable; a failure to *annotate* it does not change
  // that, so the outcome stays `queued` either way.
  await keepForRetry(dispatch, record, code).catch(() => undefined);
  return result(record.clientPunchUuid, 'queued');
}

/**
 * Boot (`TRACK-D`: "rehydrate the queue from IndexedDB into Redux").
 *
 * Also un-sticks any row a killed process left marked `sending`: nothing owns
 * it any more, and a row nobody will ever pick up is a lost punch.
 */
export const rehydrateQueue = createAsyncThunk<
  { pending: number; failed: number },
  void,
  ThunkConfig
>('offlineQueue/rehydrate', async (_arg, { dispatch, getState }) => {
  const owner = currentOwner(getState);
  // Boot before a session exists, or after a sign-out: the previous worker's
  // rows stay in IndexedDB and the mirror stays empty. Nothing to show, and
  // nothing anyone could accidentally flush.
  if (owner === null) {
    dispatch(queueCleared());
    dispatch(onlineChanged(isOnline()));
    return { pending: 0, failed: 0 };
  }

  const stale = (await listQueuedPunches(owner)).filter((record) => record.state === 'sending');
  for (const record of stale) {
    await patchQueuedPunch(record.clientPunchUuid, { state: 'pending' });
  }

  const [pending, failed] = await Promise.all([listQueuedPunches(owner), listFailedPunches(owner)]);
  dispatch(
    queueRehydrated({ pending: pending.map(summarize), failed: failed.map(summarizeFailed) }),
  );
  dispatch(onlineChanged(isOnline()));
  return { pending: pending.length, failed: failed.length };
});

/**
 * The tap (`TRACK-D` "Queue lifecycle"). The row is written **before**
 * anything else can observe it — including before GPS, which is the whole
 * point: `captureGps` can burn `GPS_TIMEOUT_MS` on a permission prompt in a
 * basement, and an iOS tab killed while the phone is pocketed at second 6
 * would leave the punch nowhere at all. So the record lands with a
 * `gpsStatus: 'timeout'` placeholder — the honest reading of "no fix yet" and
 * a shape the frozen schema already accepts — and the capture is patched in
 * when (if) it arrives. A punch sent with the placeholder is a `GPS_TIMEOUT`
 * exception for a supervisor to review; a punch that was never written is a
 * wage dispute.
 *
 * It lands `sending`, not `pending`, and only flips once the capture is in.
 * `performSync` selects exactly `pending`, so those up-to-8 s make the row
 * invisible to every other flush — otherwise any response arriving inside the
 * window takes the punch with the placeholder still on it and the late patch
 * no-ops on an already-deleted row, turning a normal punch into a
 * `GPS_TIMEOUT` exception on a supervisor's queue. A tab killed mid-capture
 * leaves a stale `sending` row, which `rehydrateQueue` already un-sticks.
 */
export const enqueuePunch = createAsyncThunk<SubmitResult, EnqueueArgs, ThunkConfig>(
  'offlineQueue/enqueue',
  async (args, { dispatch, getState }) => {
    const owner = currentOwner(getState);
    if (owner === null) throw new Error('cannot queue a punch with no signed-in user');

    const clientPunchUuid = newClientPunchUuid();
    const capturedAt = buildCapturedAt();

    const record: QueuedPunch = {
      clientPunchUuid,
      ownerUserId: owner,
      type: args.type,
      ...(args.shiftId === undefined ? {} : { shiftId: args.shiftId }),
      deviceUuid: getApiClientRuntime().getDeviceUuid(),
      ...(capturedAt === null ? {} : { capturedAt }),
      gpsStatus: 'timeout',
      attempts: 0,
      state: 'sending',
      // D-10: the server's clock, never the device's. `queuedAt` is the
      // ordering fallback for a row with no `capturedAt`, so a raw
      // `new Date()` here is the one clock we are not allowed to sort on.
      queuedAt: nowIso(),
      anchorReconstructed: isAnchorReconstructed(),
    };

    // IndexedDB first. Always.
    await putQueuedPunch(record);
    dispatch(punchQueued(summarize(record)));

    // Now the slow part, with the punch already durable. A GPS failure only
    // rewrites the placeholder with the real reason; it never refuses (D-09).
    const capture = await captureGps({ timeoutMs: GPS_TIMEOUT_MS });
    // One write: the coordinates and the release of the `sending` lock. The
    // punch is already durable, so failing to *decorate* it is never a reason
    // to fail the punch (D-09) — but the lock is then still held, by this
    // thunk, which is why the submit below is told to ignore it.
    const released = await patchQueuedPunch(clientPunchUuid, { ...capture, state: 'pending' })
      .then(() => true)
      .catch(() => false);

    return dispatch(
      submitPunch(released ? clientPunchUuid : { clientPunchUuid, holdsLock: true }),
    ).unwrap();
  },
);

/**
 * A uuid, or a uuid plus "I am the caller that already holds this row's
 * `sending` lock". Only `enqueuePunch` passes the second form, and only when
 * the write that should have released the lock failed: without it the row
 * would sit `sending` — invisible to `performSync` — until the next cold
 * start, which is a delayed punch nobody asked for.
 */
type SubmitPunchArgs =
  | string
  | { readonly clientPunchUuid: string; readonly holdsLock: true };

/** One queued punch, sent once. Single-flight per row via `state: 'sending'`. */
export const submitPunch = createAsyncThunk<SubmitResult, SubmitPunchArgs, ThunkConfig>(
  'offlineQueue/submit',
  async (args, { dispatch, getState }) => {
    const clientPunchUuid = typeof args === 'string' ? args : args.clientPunchUuid;
    const holdsLock = typeof args !== 'string';
    const owner = currentOwner(getState);
    // No session, or a row belonging to the worker who used this phone before
    // this one: `gone` as far as *this* session is concerned. The row is
    // untouched in IndexedDB, waiting for its own owner.
    if (owner === null) return result(clientPunchUuid, 'gone');
    const record = await getQueuedPunch(clientPunchUuid, owner);
    if (record === null) return result(clientPunchUuid, 'gone');
    if (record.state === 'sending' && !holdsLock) return result(clientPunchUuid, 'in_flight');
    if (!isOnline()) return result(clientPunchUuid, 'queued');

    const body = record.type === 'in' ? punchInBodyOf(record) : punchOutBodyOf(record);
    if (body === null) {
      await refuseLocally(dispatch, clientPunchUuid, 'VALIDATION_FAILED');
      return result(clientPunchUuid, 'refused', { refusalCode: 'VALIDATION_FAILED' });
    }

    try {
      // Inside the `try`: an IndexedDB failure on this bookkeeping patch must
      // not reject `enqueuePunch`, because `putQueuedPunch` already succeeded
      // and the punch *is* queued. Rejecting would render the blocking refusal
      // screen over a punch that exists, and the worker's second tap would
      // carry a fresh `clientPunchUuid` that idempotency cannot dedupe.
      await patchQueuedPunch(clientPunchUuid, { state: 'sending' });
      dispatch(punchSubmitting(clientPunchUuid));

      const response = await sendPunch(dispatch, record, body);
      // Amendment #1: the flag decides, not the status code.
      const idempotentReplay = response.idempotentReplay === true;
      await deleteQueuedPunch(clientPunchUuid);
      dispatch(
        punchConfirmed({
          clientPunchUuid,
          type: record.type,
          recordedAt: response.recordedAt,
          idempotentReplay,
          confirmedAt: nowIso(),
        }),
      );
      return result(clientPunchUuid, 'confirmed', {
        recordedAt: response.recordedAt,
        idempotentReplay,
      });
    } catch (error) {
      return handleSendFailure(dispatch, record, error);
    }
  },
);

export interface SyncOutcome {
  readonly attempted: number;
  readonly accepted: number;
  readonly duplicate: number;
  readonly refused: number;
  readonly retried: number;
  readonly skipped: 'offline' | 'empty' | null;
  readonly error: string | null;
}

const NOTHING: Omit<SyncOutcome, 'skipped' | 'error'> = {
  attempted: 0,
  accepted: 0,
  duplicate: 0,
  refused: 0,
  retried: 0,
};

/**
 * One batch flush, shared by every trigger. Module-level rather than
 * store-level because two dispatches in the same tick must return the *same*
 * promise: two `/punches/sync` calls with the same rows would be answered
 * correctly (idempotency), but they would double the load on the exact
 * connection that is already struggling.
 */
let syncInFlight: Promise<SyncOutcome> | null = null;

async function performSync(dispatch: OfflineDispatch, owner: string): Promise<SyncOutcome> {
  if (!isOnline()) return { ...NOTHING, skipped: 'offline', error: null };

  const queued = await listQueuedPunches(owner);
  const batch = queued.filter((record) => record.state === 'pending');
  if (batch.length === 0) return { ...NOTHING, skipped: 'empty', error: null };

  dispatch(syncStarted());

  const sendable: QueuedPunch[] = [];
  try {
    return await sendBatch(dispatch, batch, sendable);
  } finally {
    // Any row this pass marked `sending` and did not resolve goes back to
    // `pending`. Without this a throw (an IndexedDB error, an unwrapped
    // rejection) strands rows in `sending`: `performSync` only ever selects
    // `pending`, so every later flush answers `skipped: 'empty'` — which
    // resets the backoff and lets the app report « tout est synchronisé »
    // over punches that were never sent.
    await releaseSending(dispatch, sendable);
    dispatch(syncFinished({ at: null }));
  }
}

/**
 * Puts back every row of `batch` still marked `sending`. Best effort by
 * design: it runs from a `finally` that may itself be unwinding an IndexedDB
 * failure, and throwing here would replace the real error with this one.
 */
async function releaseSending(dispatch: OfflineDispatch, batch: QueuedPunch[]): Promise<void> {
  for (const record of batch) {
    try {
      const current = await getQueuedPunch(record.clientPunchUuid, record.ownerUserId);
      if (current === null || current.state !== 'sending') continue;
      await patchQueuedPunch(record.clientPunchUuid, { state: 'pending' });
      dispatch(
        punchRetryScheduled({
          clientPunchUuid: record.clientPunchUuid,
          attempts: current.attempts,
          lastError: current.lastError ?? null,
        }),
      );
    } catch {
      // Nothing better to do: the row stays as it is and the next cold start's
      // `rehydrateQueue` un-sticks it.
    }
  }
}

async function sendBatch(
  dispatch: OfflineDispatch,
  batch: QueuedPunch[],
  sendable: QueuedPunch[],
): Promise<SyncOutcome> {
  let refused = 0;
  const items: SyncPunchItem[] = [];
  for (const record of batch) {
    const item = syncItemOf(record);
    if (item === null) {
      await refuseLocally(dispatch, record.clientPunchUuid, 'VALIDATION_FAILED');
      refused += 1;
      continue;
    }
    sendable.push(record);
    items.push(item);
  }

  if (items.length === 0) {
    dispatch(syncFinished({ at: null }));
    return { ...NOTHING, refused, skipped: null, error: null };
  }

  for (const record of sendable) {
    await patchQueuedPunch(record.clientPunchUuid, { state: 'sending' });
    dispatch(punchSubmitting(record.clientPunchUuid));
  }

  let response: PunchSyncResponse;
  const request = dispatch(punchApi.endpoints.syncPunches.initiate({ punches: items }));
  try {
    response = await request.unwrap();
  } catch (error) {
    const code = isApiError(error) ? error.code : 'SERVICE_UNAVAILABLE';
    for (const record of sendable) await keepForRetry(dispatch, record, code);
    dispatch(syncFinished({ at: null }));
    return {
      ...NOTHING,
      attempted: sendable.length,
      retried: sendable.length,
      skipped: null,
      error: code,
    };
  } finally {
    request.reset();
  }

  let accepted = 0;
  let duplicate = 0;
  const answered = new Set<string>();
  const sent = new Set(sendable.map((record) => record.clientPunchUuid));

  for (const item of response.results) {
    // The response is answered *about the batch that was sent*. `moveToFailed`
    // and `deleteQueuedPunch` are keyed on the uuid alone and are not
    // owner-scoped, so acting on a uuid this batch never carried would delete
    // another worker's queued punch off a shared plant phone. The frozen
    // schema validates the shape of an item, never its membership.
    if (!sent.has(item.clientPunchUuid)) continue;
    answered.add(item.clientPunchUuid);
    if (item.status === 'refused') {
      // I-7: a refusal is never dropped, even when it arrives inside a batch.
      await refuseLocally(dispatch, item.clientPunchUuid, item.code, item.message);
      refused += 1;
      continue;
    }
    await deleteQueuedPunch(item.clientPunchUuid);
    dispatch(punchSynced(item.clientPunchUuid));
    if (item.status === 'accepted') accepted += 1;
    else duplicate += 1;
  }

  // A row the server did not answer for stays queued — silence is not consent.
  let retried = 0;
  for (const record of sendable) {
    if (answered.has(record.clientPunchUuid)) continue;
    await keepForRetry(dispatch, record, 'NO_RESULT');
    retried += 1;
  }

  dispatch(syncFinished({ at: nowIso() }));
  return {
    attempted: sendable.length,
    accepted,
    duplicate,
    refused,
    retried,
    skipped: null,
    error: null,
  };
}

/** `POST /punches/sync` for the whole queue, in capture-time order (D-10). */
export const syncQueue = createAsyncThunk<SyncOutcome, void, ThunkConfig>(
  'offlineQueue/sync',
  async (_arg, { dispatch, getState }) => {
    const owner = currentOwner(getState);
    // No session: the scheduler still fires on `'start'` and on `online`, and
    // the rows sitting here belong to whoever used this phone last. There is
    // no token to send them with that would not misattribute them.
    if (owner === null) return { ...NOTHING, skipped: 'empty', error: null };
    if (syncInFlight !== null) return syncInFlight;
    const run = performSync(dispatch, owner);
    syncInFlight = run;
    try {
      return await run;
    } finally {
      syncInFlight = null;
    }
  },
);

/**
 * Sign-out, with the queue accounted for (I-7).
 *
 * The plain `logout()` from the api-client ends the session and nothing else,
 * which on a shared phone is where punches go missing: the rows stay in
 * IndexedDB and the next worker's first flush sends them under *their* token.
 * So the last thing this session does is try, once, to empty its own queue —
 * and whatever is still pending afterwards stays stored under this owner,
 * invisible and unsendable to everyone else, until they sign back in here.
 *
 * `stillPending` is what the sign-out screen tells the worker: how many of
 * their punches are leaving the device unsent.
 */
export const signOut = createAsyncThunk<{ flushed: boolean; stillPending: number }, void, ThunkConfig>(
  'offlineQueue/signOut',
  async (_arg, { dispatch, getState }) => {
    const owner = currentOwner(getState);
    let flushed = false;

    if (owner !== null && isOnline()) {
      const pending = await listQueuedPunches(owner).catch(() => []);
      if (pending.length > 0) {
        // One attempt, and its failure is not an obstacle to signing out: the
        // rows survive either way.
        await dispatch(syncQueue()).unwrap().catch(() => undefined);
        flushed = true;
      }
    }

    const stillPending =
      owner === null ? 0 : (await listQueuedPunches(owner).catch(() => [])).length;

    await dispatch(logout());
    // The mirror is per-session: the next worker must not inherit a count,
    // a banner or a refusal that is not theirs.
    dispatch(queueCleared());
    return { flushed, stillPending };
  },
);

/**
 * How many punches are stored on this device for *other* users — rows the
 * current session can neither see nor send. The login screen shows it so a
 * queue that outlived a sign-out is never silently invisible.
 */
export const countForeignPending = createAsyncThunk<number, void, ThunkConfig>(
  'offlineQueue/countForeignPending',
  async (_arg, { getState }) => {
    const owner = currentOwner(getState);
    const all = await listAllQueuedPunches();
    return all.filter((record) => record.ownerUserId !== owner).length;
  },
);

/** Test-only: drop the shared in-flight promise between cases. */
export function resetSyncInFlight(): void {
  syncInFlight = null;
}

/**
 * The worker has seen the refusal. The row is marked, never deleted: it is the
 * only record that the punch was ever attempted.
 */
export const acknowledgeFailed = createAsyncThunk<string, string, ThunkConfig>(
  'offlineQueue/acknowledgeFailed',
  async (clientPunchUuid, { dispatch }) => {
    await acknowledgeFailedPunch(clientPunchUuid, nowIso());
    dispatch(failedAcknowledged(clientPunchUuid));
    return clientPunchUuid;
  },
);

export interface ContextResult {
  readonly context: MeContextResponse | null;
  readonly source: 'network' | 'cache' | 'none';
  readonly cachedAt: string | null;
}

/**
 * `GET /me/context` with the IndexedDB mirror behind it. RTK Query's cache
 * does not survive the cold start iOS does routinely, so the last good payload
 * is written to `contextCache` and read back when the network is gone.
 *
 * The cached copy is **advisory** — `punchability` from hours ago is not
 * authorization, and the punch itself is always a real request (§2 of the API
 * contract).
 */
export const refreshContext = createAsyncThunk<ContextResult, void, ThunkConfig>(
  'offlineQueue/refreshContext',
  async (_arg, { dispatch, getState }) => {
    // No session, no cache — in either direction. The payload names a worker's
    // shift, site and supervisors, so an unowned write is a row the next
    // worker on this phone would read as their own (H-2).
    const owner = currentOwner(getState);
    const request = dispatch(
      punchApi.endpoints.getMeContext.initiate(undefined, { forceRefetch: true }),
    );
    try {
      const context = await request.unwrap();
      const cachedAt = nowIso();
      if (owner !== null) await writeCachedContext(context, cachedAt, owner);
      return { context, source: 'network', cachedAt };
    } catch {
      const cached = owner === null ? null : await readCachedContext(owner);
      if (cached === null) return { context: null, source: 'none', cachedAt: null };
      return { context: cached.payload, source: 'cache', cachedAt: cached.cachedAt };
    } finally {
      request.unsubscribe();
    }
  },
);
